import { produce } from "immer";
import { merge } from "lodash";
import React, { PropsWithChildren, createContext, useContext } from "react";

import { useHashMemo } from "@examspark/common-ui";

import { MarkBlocksConfig } from "../plugins/MarkBlocks";

export type ItemEditorConfig = { additionalContext?: boolean } & (
  | { gaps: true; hottext?: false }
  | { hottext: true; gaps?: false }
  | { gaps?: never; hottext?: never }
);

export type RichContentTools = {
  advancedFormatting?: boolean;
  blockFormatting?: boolean;
  math?: boolean;
  sharedResources?: boolean;
  imageUpload?: boolean;
  itemEditor?: ItemEditorConfig;
  markBlocks?: MarkBlocksConfig;
  htmlEditor?: boolean;
};

export type RichContentOptions = {
  singleLine?: boolean;
  allowUnsupportedHtml?: "none" | "minimal" | "all";
  detectUnsupportedHtml?: boolean;
  tools?: RichContentTools;
};

export type RichContentOptionsProducer = (
  parentOptions: RichContentOptions,
) => RichContentOptions | void;

const RichContentOptionsContext = createContext<RichContentOptions>({});

export const useRichContentOptions = (
  options?: RichContentOptions | RichContentOptionsProducer,
) => {
  const parentOptions = useContext(RichContentOptionsContext);

  let mergedOptions: RichContentOptions;

  if (typeof options === "function") {
    mergedOptions = produce(parentOptions, options);
  } else if (options != null) {
    mergedOptions = merge({}, parentOptions, options);
  } else {
    mergedOptions = parentOptions;
  }

  return useHashMemo(mergedOptions);
};

type Props = {
  options: RichContentOptions | RichContentOptionsProducer;
};

export const RichContentOptionsProvider = ({
  children,
  ...props
}: PropsWithChildren<Props>) => {
  const options = useRichContentOptions(props.options);

  return (
    <RichContentOptionsContext.Provider value={options}>
      {children}
    </RichContentOptionsContext.Provider>
  );
};
